import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { SurveyScore } from "webportalclient";
import { SurveyAnswer } from "webportalclient";
import { SurveySession } from "webportalclient";
import { ODataParams, Query, useQuery } from "../Components/Query";
import { Column, Table } from "../Components/QueryTable";
import { useSecurityContext } from "../Core/SecurityContext";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { DateTimeAccuracy, LocalizedDateTime } from "../misc/components/LocalizedDateTime";
import { NotesModal } from "../misc/components/NotesModal";

export const SurveyView = () => {
    const { id } = useParams<{ id: string }>();
    const client = useWebPortalApiClient();
    const [surveySession, setSurveySession] = React.useState<SurveySession>();
    const [notesVisible, setNotesVisible] = React.useState(false);
    const { currentUserPermissions } = useSecurityContext();

    const hasViewNotePermission = currentUserPermissions.some(x => x.Id === PermissionEnum.ViewSurveySessionNotes);

    const handleRefresh = async (params: ODataParams) => {

        const filter = `sessionId eq ${id}`;

        return await client.client.getSurveyAnswers(undefined, filter, "index asc", "session($select=id,visit;$expand=visit($select=name;$expand=subject($select=number,id;$expand=site($select=number))))", params.top, params.skip, undefined, true);
    }

    React.useEffect(() => {
        const getData = async () => {
            const surveySessionResult = await client.client.getSurveySession(id, undefined, "answers,visit($select=name;$expand=subject($select=number,id;$expand=site($select=number))),notes");
            setSurveySession(surveySessionResult);
        }

        getData();
    }, []);

    if (surveySession === null || surveySession === undefined)
        return <LoadingWithMessage message="Loading Survey..." />;

    return (
        <>
            <div className="row">
                <div className="col-sm-6">
                    <h2 className="text-primary">Survey Results</h2>
                </div>

                <div className="col-sm-6 h2">
                    <Link to="/sessions" className="btn btn-primary btn-outline pull-right">Return to List</Link>
                </div>
            </div>
            <SurveySummaryPanel siteNumber={surveySession.visit.subject.site.number} subjectNumber={surveySession.visit.subject.number} visitName={surveySession.visit.name} sessionLabel={surveySession.label} occurred={surveySession.startDateTime} />
            <SurveyScoring scores={surveySession.scores} />
            <div className="panel panel-default" data-automationid="SurveyResults">
                <Query onRefresh={handleRefresh} initialOrdering={[{ field: "index", condition: "desc" }]}>
                    <SurveyTable />
                </Query>
            </div>
            {hasViewNotePermission ? <button disabled={false} className="btn btn-primary" onClick={() => setNotesVisible(true)} data-automationid="NotesButton">
                Notes&nbsp;
                <span className="badge badge-info">{surveySession.notes.length}</span>
            </button> : null}
            {hasViewNotePermission ? <NotesModal
                isVisible={notesVisible}
                header="Survey Notes"
                isAddingNote={false}
                notes={surveySession.notes}
                onCloseModal={() => setNotesVisible(false)}
                disabled={true}
            /> : null}
        </>
    )
}

export interface SurveySummaryPanelProps {
    siteNumber: string;
    subjectNumber: string;
    visitName: string;
    sessionLabel: string;
    occurred: string;
}

const SurveySummaryPanel = ({ siteNumber, subjectNumber, visitName, sessionLabel, occurred }: SurveySummaryPanelProps) => {
    return (
        <>
            <div className="panel panel-default" data-automationid="SubjectSummaryPanel">
                <div className="panel-body">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Site Number</th>
                                <th>Subject Number</th>
                                <th>Visit</th>
                                <th>Session</th>
                                <th>Occurred</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{siteNumber}</td>
                                <td>{subjectNumber}</td>
                                <td>{visitName}</td>
                                <td>{sessionLabel}</td>
                                <td><LocalizedDateTime time={occurred} accuracy={DateTimeAccuracy.Second} /></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    )
}

interface SurveyScoringProps {
    scores: SurveyScore[];
}

const SurveyScoring = ({ scores }: SurveyScoringProps) => {
    if (scores.length === 0)
        return null;

    return (
        <div className="panel panel-default" data-automationid="SurveyScores">
            <div className="panel-body">
            <h3 className="text-primary">Scores</h3>
                {scores.map(s => (<div key={s.name} className="panel panel-default col-sm-3">
                    <div className="panel-heading">
                        <b>{s.name}</b>
                    </div>
                    <div className="panel-body">
                        <strong className="lead">{s.value}</strong>
                    </div>
                </div>))}
            </div>
        </div>
    );
}

const SurveyTable = () => {
    const { data } = useQuery();

    const answerFormatter = (answer: SurveyAnswer) => {
        if (answer.valueType === "DATEPICKER")
            return <LocalizedDateTime time={answer.value} accuracy={DateTimeAccuracy.Day} />
        else if (answer.valueType === "TIMEPICKER") {
            return <LocalizedDateTime time={answer.value} accuracy={DateTimeAccuracy.TimeOnlyNoOffset} />
        } else if (answer.valueType === "DATETIMEPICKER") {
            return <LocalizedDateTime time={answer.value} accuracy={DateTimeAccuracy.Second} />
        } else
            return answer.value !== answer.valueText ? `${answer.value} ${answer.valueText ?? ""}` : answer.valueText;
    }

    return (
        <div className="panel panel-default" data-automationid="SurveyTablePanel">
            <div className="panel-body">
                <Table<SurveyAnswer> data={data}>
                    <Column field="" header="Question" wrapText={true} formatter={q => `Q${q.questionNumber ?? ""} ${q.questionText ?? ""}`} />
                    <Column field="" header="Answer" wrapText={true} formatter={answerFormatter} />
                </Table>
            </div>         
        </div>
    )
}
